// types.
import { LeadDetail } from '@domains/lead-detail/interfaces/interfaces';
import { GeoreferencingOverrides } from '../interfaces/georeferencing.types';
import { buildChangeCoordinatesPayload } from './buildChangeCoordinatesPayload';

export interface LoadingHandlers {
  setIsLoading: (loading: boolean) => void;
  refetchLeadDetail: () => void;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

export interface GeoreferencingCallbacks {
  handleGeoreferencingSuccess: (
    setIsLoading: (loading: boolean) => void,
    refetchLeadDetail: () => void,
    onSuccess?: () => void
  ) => void;
  handleGeoreferencingError: (
    setIsLoading: (loading: boolean) => void,
    errorMessage: string,
    onError?: (error: string) => void
  ) => void;
}

export interface LeadData {
  leadDetail: LeadDetail | undefined;
  coordinates: google.maps.LatLngLiteral | null;
  email: string;
  country: string | undefined;
  overrides?: GeoreferencingOverrides;
}

export interface ProcessWebSocketMessageParams {
  parsedBody: any;
  selectedBusinessLine: string | null;
  loadingHandlers: LoadingHandlers;
  callbacks: GeoreferencingCallbacks;
  leadData: LeadData;
  saveHermes: any;
}

const handleInmoFlow = (
  loadingHandlers: LoadingHandlers,
  callbacks: GeoreferencingCallbacks
) => {
  callbacks.handleGeoreferencingSuccess(
    loadingHandlers.setIsLoading,
    loadingHandlers.refetchLeadDetail,
    loadingHandlers.onSuccess
  );
};

const handleMarketMakerFlow = async (
  leadData: LeadData,
  saveHermes: any,
  loadingHandlers: LoadingHandlers,
  callbacks: GeoreferencingCallbacks
) => {
  if (!leadData.leadDetail) {
    throw new Error('No hay datos del lead disponibles');
  }
  if (!leadData.coordinates) {
    throw new Error('No hay coordenadas seleccionadas');
  }
  if (!leadData.email) {
    throw new Error('No se ha obtenido el email del agente');
  }

  const changeCoordPayload = buildChangeCoordinatesPayload(
    leadData.leadDetail,
    leadData.coordinates,
    leadData.email,
    leadData.country,
    leadData.overrides
  );

  await saveHermes(changeCoordPayload).unwrap();
  callbacks.handleGeoreferencingSuccess(
    loadingHandlers.setIsLoading,
    loadingHandlers.refetchLeadDetail,
    loadingHandlers.onSuccess
  );
};

export const processWebSocketMessage = async ({
  parsedBody,
  selectedBusinessLine,
  loadingHandlers,
  callbacks,
  leadData,
  saveHermes,
}: ProcessWebSocketMessageParams): Promise<void> => {
  const isGeoreferencingStep = parsedBody?.step === 'georeferencing';

  if (!isGeoreferencingStep) {
    return;
  }

  if (parsedBody?.success === false) {
    callbacks.handleGeoreferencingError(
      loadingHandlers.setIsLoading,
      parsedBody?.error || 'Error en georreferenciación',
      loadingHandlers.onError
    );
    return;
  }

  if (parsedBody?.success !== true) {
    return;
  }

  // Success case - diferenciado por business line
  if (selectedBusinessLine === 'Inmo') {
    handleInmoFlow(loadingHandlers, callbacks);
  } else if (selectedBusinessLine === 'Market-Maker') {
    try {
      await handleMarketMakerFlow(leadData, saveHermes, loadingHandlers, callbacks);
    } catch (error: any) {
      console.error('Error en /change_coordinates:', error);
      callbacks.handleGeoreferencingError(
        loadingHandlers.setIsLoading,
        error?.message || 'Error al guardar en change_coordinates',
        loadingHandlers.onError
      );
    }
  }
};

